import { Avatar, Box } from '@mantine/core';
import { getProviderRoleAbbreviation, getProviderRoleLabel, ProviderRole } from '@bariendo/lib/constants';
import { getRoleColor } from '@/theme/roleColors';
import { ReactElement } from 'react';

/**
 * A team avatar: the role's abbreviation (PAC, CC, NP, RD, MD-S …) on a solid
 * role-color fill. This is the avatar for "the Care Coordinator team" rather
 * than for one coordinator — a person keeps their initials and takes the role
 * color as a ring instead (see TeammateAvatar).
 *
 * Roles outside the five color groups (admin, BE, EQA) fall back to the
 * neutral gray fill the task panel always used. Carries no tooltip of its
 * own: the slot it sits in owns hover (the task row's complete-task tooltip,
 * the select option's label).
 */
export interface RoleAvatarProps {
  role: ProviderRole | undefined;
  /** Diameter in px. 28 in the task row, 18 in the assignee select. */
  size: number;
}

/** Type sizes the two shipped diameters were tuned at; only 4-glyph codes (MD-S) drop 2px — PAC stays full size. */
function abbreviationFontSize(size: number, abbreviation: string): number {
  const base = size >= 24 ? 11 : 8;
  return abbreviation.length > 3 ? base - 2 : base;
}

export function RoleAvatar({ role, size }: RoleAvatarProps): ReactElement {
  const color = getRoleColor(role);
  const abbreviation = getProviderRoleAbbreviation(role);

  // No role → a neutral blank disc. Mantine would otherwise fall through empty
  // children to its person silhouette, which reads as "assigned to someone".
  if (!abbreviation) {
    return (
      <Box
        component="span"
        aria-hidden
        style={{
          display: 'inline-block',
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: 'var(--mantine-color-gray-1)',
          flexShrink: 0,
        }}
      />
    );
  }

  return (
    <Avatar
      size={size}
      radius="xl"
      aria-label={getProviderRoleLabel(role) || undefined}
      styles={{
        root: {
          backgroundColor: color?.fill ?? 'var(--mantine-color-gray-1)',
          flexShrink: 0,
        },
        placeholder: {
          backgroundColor: 'transparent',
          color: color?.text ?? 'var(--mantine-color-gray-7)',
          fontSize: abbreviationFontSize(size, abbreviation),
          fontWeight: 600,
          letterSpacing: abbreviation.length > 3 ? -0.2 : 0,
        },
      }}
    >
      {abbreviation}
    </Avatar>
  );
}
